import { ButtonLink } from '@designsforhealth/dfh-react-components'
import { Grid, TextField } from '@material-ui/core'
import classNames from 'classnames'
import React, { useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled, { css, DefaultTheme, ThemeProps } from 'styled-components'

import { FormValues } from '../../../lib/cart/types'
import { setValuesWith } from '../../../utils/formTools'
import useStyles from './styles/addressForm'

interface ReadOnlyTextFieldProps {
  label: string
  name: string
}

const ReadOnlyTextField: React.FC<ReadOnlyTextFieldProps> = ({ label, name }) => {
  // use Controller instead of `register` since inputs do not register when toggling "Billing
  // address is the same as shipping address"
  const { control } = useFormContext()

  return (
    <Controller
      render={({ onBlur, onChange, ref, value }) => (
        <TextField
          disabled
          fullWidth
          label={label}
          inputRef={ref}
          InputLabelProps={{
            // proactively shrink label since component isn't controlled
            shrink: true,
          }}
          onBlur={onBlur}
          onChange={onChange}
          name={name}
          value={value}
          variant="filled"
        />
      )}
      control={control}
      name={name}
    ></Controller>
  )
}

const StyledOverlayButton = styled(ButtonLink)(
  ({ theme }: ThemeProps<DefaultTheme>) => css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transition: all 0.2s;
    opacity: 0;
    background: ${theme.colors.coolGray.cool700};
    font-size: 24px;
    font-weight: 600;
    color: ${theme.colors.grayscale.white};
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      opacity: 0.4;
    }
  `,
)

export interface ReadOnlyAddressFormProps {
  className?: string
  clickToEditText?: string
  defaultValues?: FormValues.Address
  onClickToEdit?: () => void
  showEmail?: boolean
}

const ReadOnlyAddressForm: React.FC<ReadOnlyAddressFormProps> = ({
  className,
  clickToEditText = 'Click to edit address',
  defaultValues,
  onClickToEdit,
  showEmail = false,
}) => {
  const classes = useStyles()
  const { register, setValue } = useFormContext<FormValues.Address>()

  useEffect(() => {
    if (!defaultValues) {
      return
    }

    const setDefaultAddressValues = (): void => {
      const setValues = setValuesWith(setValue)
      setValues(defaultValues)
    }

    window.requestAnimationFrame(setDefaultAddressValues)
  }, [defaultValues, setValue])

  return (
    <Grid
      className={classNames(className, classes.addressForm)}
      container
      spacing={2}
      alignItems="center"
      justifyContent="center"
    >
      {showEmail && (
        <Grid item xs={12} sm={12}>
          <ReadOnlyTextField label="Email Address" name="email" />
        </Grid>
      )}
      <Grid item xs={6} sm={6}>
        <ReadOnlyTextField label="First Name" name="firstname" />
      </Grid>
      <Grid item xs={6} sm={6}>
        <ReadOnlyTextField label="Last Name" name="lastname" />
      </Grid>
      <Grid item xs={12} sm={12}>
        <ReadOnlyTextField label="Address" name="street1" />
      </Grid>
      <Grid item xs={12} sm={12}>
        <ReadOnlyTextField label="Apartment, unit, suite, etc. (optional)" name="street2" />
      </Grid>
      <Grid item xs={6} sm={6}>
        <ReadOnlyTextField label="City" name="city" />
      </Grid>
      <Grid item xs={6} sm={6}>
        <ReadOnlyTextField label="State" name="region" />
        <input type="hidden" name="region_id" ref={register} />
      </Grid>
      <Grid item xs={4} sm={6}>
        <ReadOnlyTextField label="Zip Code" name="postcode" />
      </Grid>
      <Grid item xs={8} sm={6}>
        <ReadOnlyTextField label="Country" name="country_code" />
      </Grid>
      <Grid item xs={12} sm={12}>
        <ReadOnlyTextField label="Phone" name="telephone" />
      </Grid>
      {onClickToEdit && (
        <StyledOverlayButton onClick={onClickToEdit}>
          <div>{clickToEditText}</div>
        </StyledOverlayButton>
      )}
    </Grid>
  )
}

const StyledReadOnlyAddressForm = styled(ReadOnlyAddressForm)`
  position: relative;
`

export default StyledReadOnlyAddressForm
